import axios from 'axios'

export function decodeHtmlEntities(text) {
  if (!text) return ''
  return String(text)
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCharCode(parseInt(h, 16)))
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, ' ')
}

export function digestDisplaySummary(item) {
  const raw = item?.title || item?.summary_en || ''
  return decodeHtmlEntities(raw)
}

export function normalizeDigestText(text) {
  return decodeHtmlEntities(String(text || ''))
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

export const DIGEST_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'members', label: 'Members', icon: 'members' },
  { id: 'deal', label: 'Deals', icon: 'fund' },
  { id: 'market', label: 'Market', icon: 'chart' },
  { id: 'policy', label: 'Policy', icon: 'policy' },
  { id: 'report', label: 'Reports', icon: 'report' },
]

export const DIGEST_TYPES = {
  deal: { icon: 'fund', label: 'Deal', iconClass: 'deal', labelClass: 'deal' },
  market: { icon: 'chart', label: 'Market', iconClass: 'market', labelClass: 'market' },
  policy: { icon: 'policy', label: 'Policy', iconClass: 'policy', labelClass: 'policy' },
  report: { icon: 'report', label: 'Report', iconClass: 'report', labelClass: 'report' },
}

const TYPE_RULES = [
  { type: 'deal', re: /\b(deal|acquire|acquisition|merger|financing|round|invest|ipo|take-private|stake|exit|buyout)\b/i },
  { type: 'policy', re: /\b(policy|regulation|csrc|sec|qflp|regulatory|compliance|pilot)\b/i },
  { type: 'report', re: /\b(report|snapshot|study|survey|data shows|analytics|according to|preqin|pitchbook)\b/i },
  { type: 'market', re: /\b(market|fundraising|fund flow|credit|private equity|pe\/vc|returns|volume|fundraising)\b/i },
]

export function classifyDigestItem(item) {
  const text = `${item.title || ''} ${item.summary_zh || ''}`
  for (const { type, re } of TYPE_RULES) {
    if (re.test(text)) return type
  }
  return 'market'
}

export function digestGreeting(name) {
  const hour = new Date().getHours()
  const part = hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening'
  const first = (name || 'Partner').trim().split(/\s+/)[0] || 'Partner'
  const brief = hour < 12 ? 'Morning Brief' : 'Evening Brief'
  return { greeting: `Good ${part}, ${first}`, briefLabel: brief }
}

export function formatDigestTime(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return `${time} · ${date}`
}

export function formatDigestDate(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

export function langBadge(lang) {
  const code = (lang || 'en').toLowerCase()
  if (code.startsWith('zh') || code === 'cn') return { label: 'Chinese', className: 'zh' }
  return { label: 'EN', className: 'en' }
}

export function normalizeDigestFeedResponse(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.items)) return data.items
  return []
}

/** Load digest feed — authenticated first, public feed as fallback. */
export async function fetchDigestFeed(limit = 50) {
  const params = { limit }
  try {
    const res = await axios.get('/api/digest/feed/me', { params })
    const rows = normalizeDigestFeedResponse(res.data)
    if (rows.length) return rows
  } catch {
    /* fall through to public feed */
  }
  const res = await axios.get('/api/digest/feed', { params })
  return normalizeDigestFeedResponse(res.data)
}

export function markDigestRead(id) {
  return axios.post(`/api/digest/items/${id}/read`).then(() => {
    window.dispatchEvent(new Event('rs-digest-read-changed'))
    window.dispatchEvent(new Event('rs-notifications-changed'))
  })
}

export function markAllDigestRead() {
  return axios.post('/api/digest/mark-all-read').then(() => {
    window.dispatchEvent(new Event('rs-digest-read-changed'))
    window.dispatchEvent(new Event('rs-notifications-changed'))
  })
}

/** One-time migration from legacy localStorage reads to server. */
export function syncLegacyDigestReads() {
  try {
    const raw = localStorage.getItem('rs-digest-read')
    if (!raw) return Promise.resolve()
    const ids = JSON.parse(raw)
    if (!Array.isArray(ids) || !ids.length) {
      localStorage.removeItem('rs-digest-read')
      return Promise.resolve()
    }
    return axios.post('/api/digest/sync-reads', { ids }).finally(() => {
      localStorage.removeItem('rs-digest-read')
    })
  } catch {
    localStorage.removeItem('rs-digest-read')
    return Promise.resolve()
  }
}

export function groupDigestItems(items) {
  const groups = []
  const map = new Map()

  for (const item of items) {
    const d = new Date(item.published_at || item.fetched_at)
    const dateKey = Number.isNaN(d.getTime()) ? 'unknown' : d.toISOString().slice(0, 10)
    const period = Number.isNaN(d.getTime()) || d.getHours() < 12 ? 'morning' : 'evening'
    const key = `${dateKey}-${period}`
    if (!map.has(key)) {
      const group = {
        key,
        dateKey,
        period,
        periodLabel: period === 'morning' ? 'Morning Brief' : 'Evening Brief',
        dateLabel: formatDigestDate(d),
        items: [],
      }
      map.set(key, group)
      groups.push(group)
    }
    map.get(key).items.push(item)
  }

  return groups
}
